/**
 * Prop 65 geo reveal (brief ccc-prop65-geo-gate, Signal B).
 *
 * snippets/prop65-warning.liquid renders the warning with [hidden][data-prop65-geo]
 * for shoppers it can't place in California server-side. This reveals every such
 * element when the visitor's IP resolves to California.
 *
 * - Provider: get.geojs.io (HTTPS, no key); region === 'California'.
 * - One lookup per session, cached in sessionStorage 'so_region' ('CA' | 'other').
 *   No storage (private mode, blocked): falls back to one lookup per page.
 * - No lookup at all unless a [data-prop65-geo] element is on the page, so pages
 *   without a gated warning (merch, empty cart, CA customer) make no request.
 * - Error or 2 s timeout: stays hidden, nothing cached, console.warn only.
 *   John's call; the footer link to /pages/california-prop-65 is the backstop.
 * - Cart drawer: Horizon re-renders it on 'cart:update' (ThemeEvents.cartUpdate)
 *   by morphing the section, which can add new nodes or put [hidden] back on
 *   existing ones, so a MutationObserver re-applies after every change.
 * - Testing: ?prop65=ca forces reveal, ?prop65=off forces hide, for this session only.
 */
(() => {
  if (window.__soProp65Geo) return;
  window.__soProp65Geo = true;

  const KEY = 'so_region';
  const SEL = '[data-prop65-geo]';
  const GEO_URL = 'https://get.geojs.io/v1/ip/geo.json';
  let memory = null;

  const read = () => {
    try { return window.sessionStorage.getItem(KEY); } catch (e) { return memory; }
  };
  const write = (value) => {
    memory = value;
    try { window.sessionStorage.setItem(KEY, value); } catch (e) { /* storage unavailable: per-page */ }
  };

  const override = new URLSearchParams(window.location.search).get('prop65');
  if (override === 'ca') write('CA');
  else if (override === 'off') write('other');

  let region = read();
  let pending = null;

  const reveal = () => {
    document.querySelectorAll(SEL).forEach((el) => {
      if (el.hasAttribute('hidden')) el.removeAttribute('hidden');
    });
  };

  const lookup = () => {
    if (pending) return pending;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 2000);
    pending = fetch(GEO_URL, { signal: controller.signal, credentials: 'omit', cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        region = data && data.region === 'California' ? 'CA' : 'other';
        write(region);
      })
      .catch((err) => {
        console.warn('[prop65] geo lookup failed; California warning stays hidden.', (err && err.name) || err);
      })
      .finally(() => window.clearTimeout(timer));
    return pending;
  };

  const apply = () => {
    if (!document.querySelector(SEL)) return;
    if (region === 'CA') { reveal(); return; }
    if (region === 'other') return;
    lookup().then(() => { if (region === 'CA') reveal(); });
  };

  // setTimeout, not requestAnimationFrame: rAF doesn't run in background or hidden
  // tabs, and the reveal shouldn't wait for a paint.
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    window.setTimeout(() => { queued = false; apply(); }, 0);
  };

  document.addEventListener('cart:update', schedule);
  new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === 'attributes' || r.addedNodes.length) { schedule(); return; }
    }
  }).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden'] });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
  else apply();
})();
